import {
  AllowanceTypesToHandle,
  convertToDecimals,
  currency,
  distanceUnit,
  getTypeOfUser,
  ICounterOffer,
  IPharmacistDetails,
  IPharmacistEvent,
  IWorkshift,
  TypeOfUser,
} from '@pharmaplan/common';

import useAdmin from '../useAdmin';
import strings from '../../localization';
import { useAppSelector } from '../useAppSelector';
import { handleValues } from '../../components/SelfService/PharmacistSummary/helpers';
import useAllowanceWorkshiftDropdowns from '../workshift/useAllowanceWorkshiftDropdowns';

const { pharmacy } = TypeOfUser;
const { paidAirTicket, paidValuePerNight } = AllowanceTypesToHandle;

const useBillDetails = (
  event:
    | Partial<IPharmacistDetails & IWorkshift>
    | IPharmacistEvent
    | ICounterOffer,
  isAutoSyncRecord?:boolean,
) => {
  const { isAdmin } = useAdmin();
  const userType = useAppSelector(getTypeOfUser);
  const isPharmacy = userType === pharmacy;

  const {
    travelAllowance,
    mealAllowance,
    accommodationAllowance,
    travelAllowanceTypes,
    cost,
    emergencyFees,
    accommodationAllowanceType,
    pharmacyId,
    otherAllowance,
    counterEmergencyFees,
  } = event ?? {};

  const {
    counterCost = 0,
    counterMealAllowance = 0,
    counterAccommodationAllowance = 0,
    counterAccommodationAllowanceType,
    counterTravelAllowanceType = [],
    counterTravelAllowance = 0,
    counterTravelAllowanceAmount,
    counterOtherAllowance,
    counterTravelAllowanceRatePerKm,
  } = (event as ICounterOffer) ?? {};

  const { travelAllowanceRatePerKm, travelAllowanceAmount } = event as IWorkshift;

  const { travelDropdowns, accommodationDropdowns } = useAllowanceWorkshiftDropdowns({ pharmacyId, isAutoSyncRecord, useApi: true });

  const getUserBasedAllowances = () => {
    const paidAirTicketGiven = travelAllowanceTypes?.includes(paidAirTicket)
      && travelDropdowns?.find((item) =>
        item.key === paidAirTicket)?.value;

    const counterPaidAirTicket = counterTravelAllowanceType?.includes(paidAirTicket)
      && travelDropdowns?.find((item) =>
        item.key === paidAirTicket)?.value;

    const travelPerKm = !!travelAllowanceRatePerKm
      && `$${travelAllowanceRatePerKm}/${distanceUnit} `;

    const counterTravelPerKm = !!counterTravelAllowanceRatePerKm
      && `$${counterTravelAllowanceRatePerKm}/${distanceUnit}`;

    const pharmacyCounterTravelDisplay = handleValues([
      counterTravelAllowanceAmount,
      counterTravelPerKm,
      counterPaidAirTicket,
    ]);
    const pharmacyTravelDisplay = handleValues([
      travelAllowanceAmount,
      travelPerKm,
      paidAirTicketGiven,
    ]);

    return {
      travelDisplay: pharmacyTravelDisplay,
      counterTravelDisplay: pharmacyCounterTravelDisplay,
    };
  };

  const { travelDisplay, counterTravelDisplay } = getUserBasedAllowances();

  const accommodationPerNight = !!accommodationAllowance && `$${accommodationAllowance} /${strings.night}`;

  const accommodationLabels = accommodationAllowanceType !== paidValuePerNight
    && accommodationDropdowns?.find(
      (item) =>
        item.key === accommodationAllowanceType,
    )?.value;

  const counterAccommodationLabels = counterAccommodationAllowanceType !== paidValuePerNight
    && accommodationDropdowns?.find(
      (item) =>
        item.key === counterAccommodationAllowanceType,
    )?.value;

  const accommodationDisplay = handleValues([
    accommodationPerNight,
    accommodationLabels,
  ]);

  const suffix = isPharmacy ? ` + ${strings.tax}` : '';

  const valuesToShow = {
    travelData: !!travelAllowanceTypes?.length,
    accommodationData: !!accommodationAllowanceType,
  };

  const { travelData, accommodationData } = valuesToShow;

  let contractValue = `$${convertToDecimals(cost)}${suffix}`;
  let counterOfferValue = `$${convertToDecimals(counterCost)}`;

  if (counterCost) {
    contractValue = `$${convertToDecimals(cost)}`;
    counterOfferValue = `$${convertToDecimals(counterCost)}${suffix}`;
  }

  const bookingDetailsConfig = [
    {
      key: '1',
      name: isAdmin ? strings.pharmacistContractValue : strings.contractValue,
      value: contractValue,
      data: cost,
      amount: cost,
      counterOfferAmount: counterCost,
      counterOfferData: counterCost,
      counterOfferValue,
      // manage this when we start working on self-service
      // customStyle: isPharmacist ? styles.contractValue : {},
    },
    {
      key: '2',
      name: strings.meal,
      value: `${currency}${convertToDecimals(mealAllowance)}`,
      data: !!mealAllowance,
      amount: mealAllowance,

      // Counter Offer
      counterOfferData: !!counterMealAllowance,
      counterOfferValue: `${currency}${convertToDecimals(
        counterMealAllowance,
      )}`,
      counterOfferAmount: counterMealAllowance,
    },
    {
      key: '3',
      name: strings.travel,
      value: travelDisplay,
      data: travelData,
      amount: travelAllowance,

      // Counter offer
      counterOfferData: counterTravelAllowanceType?.length,
      counterOfferValue: counterTravelDisplay,
      counterOfferAmount: counterTravelAllowance,
    },
    {
      key: '4',
      name: strings.accomodation,
      value: accommodationDisplay,
      data: accommodationData,
      amount: accommodationAllowance,

      // Counter offer
      counterOfferData: handleValues([
        counterAccommodationAllowance,
        counterAccommodationLabels,
      ]),
      counterOfferValue: handleValues([
        counterAccommodationAllowance,
        counterAccommodationLabels,
      ]),
      counterOfferAmount: counterAccommodationAllowance,
    },
    {
      key: '5',
      name: strings.emergencyServiceFees,
      value: `$${convertToDecimals(emergencyFees)}`,
      counterOfferValue: `$${convertToDecimals(counterEmergencyFees)}`,
      counterOfferData: !!counterEmergencyFees,
      data: !!emergencyFees,
      amount: emergencyFees,
      counterOfferAmount: counterEmergencyFees,
    },
    {
      key: '6',
      name: strings.other,
      value: `$${convertToDecimals(otherAllowance)}`,
      counterOfferValue: `$${convertToDecimals(counterOtherAllowance)}`,
      counterOfferData: !!counterOtherAllowance,
      data: !!otherAllowance,
      amount: otherAllowance,
      counterOfferAmount: counterOtherAllowance,
    },
  ];

  return { bookingDetailsConfig };
};
export default useBillDetails;
